import { FoodsSelect } from "@foodpilot/foods";
import { Autocomplete, Box, CircularProgress, TextField, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsLoadingIndicator } from "src/components/EntityForm";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import type { IngredientFormIngredientReferenceLine } from "../../IngredientForm.types";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

type IngredientSupplierReferenceFieldProps = {
    appearance?: "legacy" | "product-v2";
    disabled?: boolean;
    isLoading?: boolean;
    onChange: (reference: IngredientFormIngredientReferenceLine | null) => void;
    options: IngredientFormIngredientReferenceLine[];
    value: IngredientFormIngredientReferenceLine | null;
};

export const IngredientSupplierReferenceField = (props: IngredientSupplierReferenceFieldProps) => {
    const { appearance, disabled = false, isLoading = false, onChange, options, value } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const featureProductUxV2 = useIsProductUxV2();
    const isProductUxV2 = appearance === "product-v2" || (appearance === undefined && featureProductUxV2);

    if (isLoading) {
        return isProductUxV2 ?
                <FoodsLoadingIndicator
                    label={t("ingredient.form.suppliers.referencesLoading", "Loading ingredient references")}
                    size={theme.spacing(2)}
                />
            :   <CircularProgress
                    aria-label={t("ingredient.form.suppliers.referencesLoading", "Loading ingredient references")}
                    size={theme.spacing(2)}
                />;
    }

    const placeholder = t("ingredient.form.suppliers.ingredientReferencePlaceholder", "No override");
    const ariaLabel = t("ingredient.form.suppliers.ingredientReferenceAriaLabel", "Per-supplier ingredient reference");

    if (!isProductUxV2) {
        return (
            <Autocomplete<IngredientFormIngredientReferenceLine>
                disabled={disabled}
                options={options}
                value={value}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, selectedValue) => option["@id"] === selectedValue["@id"]}
                onChange={(_event, newValue) => onChange(newValue)}
                size="small"
                sx={{ minWidth: theme.spacing(30) }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        inputProps={{
                            ...params.inputProps,
                            "aria-label": ariaLabel,
                        }}
                    />
                )}
            />
        );
    }

    return (
        <Box aria-label={ariaLabel} role="group" sx={{ minWidth: theme.spacing(30) }}>
            <FoodsSelectTriggerAdapter accessibleLabel={ariaLabel}>
                <FoodsSelect<IngredientFormIngredientReferenceLine>
                    options={options}
                    selectedOption={value}
                    disabled={disabled}
                    getId={(option) => option["@id"]}
                    getName={(option) => option.name}
                    onChange={onChange}
                    noValue={placeholder}
                    closeOnSelect
                    textOptions={{
                        placeholder,
                        searchPlaceholder: t("common.search", "Search"),
                        emptyOptionsText: placeholder,
                        noResult: t("common.noResults", "No results"),
                    }}
                    BoxProps={{ maxWidth: "100%" }}
                />
            </FoodsSelectTriggerAdapter>
        </Box>
    );
};
